import VueI18n, { I18nOptions, IVueI18n, LocaleMessages } from 'vue-i18n';
import da from '@/ui/translations/da.json';
import de from '@/ui/translations/de.json';
import en from '@/ui/translations/en.json';
import es from '@/ui/translations/es.json';
import fi from '@/ui/translations/fi.json';
import fr from '@/ui/translations/fr.json';
import it from '@/ui/translations/it.json';
import nl from '@/ui/translations/nl.json';
import pl from '@/ui/translations/pl.json';
import ptBR from '@/ui/translations/pt-BR.json';
import sv from '@/ui/translations/sv.json';
import tr from '@/ui/translations/tr.json';
import cs from '@/ui/translations/cs.json';
import hu from '@/ui/translations/hu.json';
import sk from '@/ui/translations/sk.json';
import ro from '@/ui/translations/ro.json';
import sl from '@/ui/translations/sl.json';
import sr from '@/ui/translations/sr.json';
import bg from '@/ui/translations/bg.json';
import hr from '@/ui/translations/hr.json';
import zhCN from '@/ui/translations/zh-CN.json';
import legacyMessages from '@/ui/translations/_legacy/index';
import { dateTimeFormats, DateTimeFormatter } from './dateTimeFormats';
import { numberFormats, NumberFormatter } from './numberFormats';

let i18n: IVueI18n | null = null;
//cannot use vue ref here
export const localeInitialised = { value: false };

export type locales = 'da' | 'de' | 'en' | 'en-GB' | 'es' | 'fi' | 'fr' | 'it' | 'nl' | 'pl' | 'pt-BR' | 'sv' | 'tr' | 'cs' | 'hu' | 'sk' | 'ro' | 'sl' | 'sr' | 'bg' | 'hr' | 'zh-CN';

export const supportedLocales: locales[] = ['da', 'de', 'en', 'en-GB', 'es', 'fi', 'fr', 'it', 'nl', 'pl', 'pt-BR', 'sv', 'tr', 'cs', 'hu', 'sk', 'ro', 'sl', 'sr', 'bg', 'hr', 'zh-CN'];

export const supportedFormLocales = [...new Set([...supportedLocales, 'sq', 'am', 'ar', 'hy', 'bn', 'bs', 'bg', 'my', 'ca', 'zh',
  'hr', 'cs', 'et', 'ka', 'el', 'gu', 'hi', 'hu', 'is', 'id', 'ja', 'kn', 'kk', 'ko', 'lv', 'lt', 'mk', 'ms', 'ml',
  'mr', 'mn', 'no', 'fa', 'pa', 'ro', 'ru', 'sr', 'sk', 'sl', 'so', 'sw', 'tl', 'ta', 'te', 'th', 'uk', 'ur', 'vi'])];

export type formLocales = typeof supportedFormLocales[number];

export const messagesParam: LocaleMessages = {
  da: {
    ...legacyMessages.da,
    ...da,
  },
  de: {
    ...legacyMessages.de,
    ...de,
  },
  en: {
    ...legacyMessages.en,
    ...en,
  },
  'en-GB': {
    ...legacyMessages.en,
    ...en,
  },
  es: {
    ...legacyMessages.es,
    ...es,
  },
  fi: {
    ...legacyMessages.fi,
    ...fi,
  },
  fr: {
    ...legacyMessages.fr,
    ...fr,
  },
  it: {
    ...legacyMessages.it,
    ...it,
  },
  nl: {
    ...legacyMessages.nl,
    ...nl,
  },
  pl: {
    ...legacyMessages.pl,
    ...pl,
  },
  'pt-BR': {
    ...legacyMessages['pt-BR'],
    ...ptBR,
  },
  sv: {
    ...legacyMessages.sv,
    ...sv,
  },
  tr: {
    ...legacyMessages.tr,
    ...tr,
  },
  cs: {
    ...legacyMessages.cs,
    ...cs,
  },
  hu: {
    ...legacyMessages.hu,
    ...hu,
  },
  sk: {
    ...legacyMessages.sk,
    ...sk,
  },
  ro: {
    ...legacyMessages.ro,
    ...ro,
  },
  sl: {
    ...legacyMessages.sl,
    ...sl,
  },
  sr: {
    ...legacyMessages.sr,
    ...sr,
  },
  bg: {
    ...legacyMessages.bg,
    ...bg,
  },
  hr: {
    ...legacyMessages.hr,
    ...hr,
  },
  'zh-CN': {
    ...legacyMessages['zh-CN'],
    ...zhCN,
  },
};

// de_DE -> de
export function fullLanguageToLocale(language: string): locales {
  if (language.match(/[a-z]-[A-Z]/)) {
    return language.split('-')[0] as locales;
  }
  return language as locales;
}

export function initialLocale(): locales {
  localeInitialised.value = true;
  return (document.querySelector('html') as HTMLElement)
    .getAttribute('lang') as locales ?? fullLanguageToLocale(window.navigator.language) ?? 'en';
}

export function createI18nInstance(messages = messagesParam, locale: locales = 'en') {
  const options: I18nOptions = {
    locale,
    fallbackLocale: 'en',
    silentTranslationWarn: true,
    silentFallbackWarn: true,
    formatFallbackMessages: true,
    messages,
    numberFormats,
    dateTimeFormats: dateTimeFormats(),
  };

  i18n = new VueI18n(options);
  localeInitialised.value = true;

  return i18n;
}

export function getI18N() {
  return i18n || createI18nInstance();
}

export function setDateTimeFormats(timeZone?: string) {
  const i18n = getI18N();
  Object.entries(dateTimeFormats(timeZone)).forEach(([locale, formats]) => {
    i18n.setDateTimeFormat(locale, formats);
  });
}

export function setLocale(locale: locales) {
  getI18N().locale = locale;
  document.documentElement.lang = locale;
  localeInitialised.value = true;
}

export function getLocale(): locales {
  return getI18N().locale as locales;
}

export function t(
  key: string,
  values?: { [key: string]: string } | undefined,
  choice?: number,
): string {
  const result = getI18N().tc(key, choice, values);

  //should never happen or be fixed, means legacy
  if (result === key && getLocale() !== 'en') {
    console.warn(`translation is missing for key: ${key}`);
  }

  return result;
}

// moved from the old app, maybe replace with something better
export const fromMillisecondsToSeconds = (value: number) => parseInt((value / 1000).toString(), 10);
export const toMillisecondsFromSeconds = (value: number) => parseInt(value.toString(), 10) * 1000;
export const toUnixTimestamp = (value: number) => fromMillisecondsToSeconds(value);
export const fromUnixTimestamp = (value: number) => toMillisecondsFromSeconds(value);
//end moved

//todo: these functions can throw RangeError on wrong date - wrap in tryCatch()
export const n = (value: number, format: NumberFormatter = 'integer') => getI18N()
  .n(value, format);
export const d = (value: Date, format: DateTimeFormatter = 'shortDateTime') => getI18N()
  .d(value, format);

//moved from the old app
export const du = (value: number | Date, format: DateTimeFormatter) => {
  let date = value;
  if ((typeof value === 'number') && Number.isInteger(value)) {
    date = new Date(fromUnixTimestamp(value));
  }
  return d(date as Date, format);
};
